import {
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
} from "@chakra-ui/react";
import { MenuItemLink, ModalConfirm } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  BankDocumentTypeEnum,
  useMarkProformaAsPaidMutation,
} from "@src/__generated__/graphql";
import { useDeleteOutgoingProformaInvoiceMutation } from "@src/__generated__/urql-graphql";
import { InvoiceTypeEnum } from "@src/components/modules/invoices/Create/store";
import { Icon } from "@src/components/ui-kit/Icon";
import { onError } from "@src/utils/apollo";
import { can, cannot } from "@src/utils/components/permissions";
import { toApiDate } from "@src/utils/dates";
import { useStore as useStores } from "@src/utils/hooks";
import React, { type FC } from "react";
import { useStore } from "../../../store";
import { MarkAsPaidButton, ResendAction } from "../../Actions";
import type { ColumnProps } from ".";

export const Actions: FC<React.PropsWithChildren<ColumnProps>> = ({ row }) => {
  const store = useStore();
  const {
    sendInvoiceModalStore,
    manualPaymentModalStore,
    matchPaymentModalStore,
    workspaceStore,
  } = useStores();
  const deleteModal = useDisclosure();
  const [{ fetching: deletingOi }, deleteOi] =
    useDeleteOutgoingProformaInvoiceMutation();

  const [markAsPaidMutation] = useMarkProformaAsPaidMutation({
    update(cache) {
      cache.evict({
        id: "ROOT_QUERY",
        fieldName: "unusedPaidOutgoingProformaInvoices",
      });
    },
    onCompleted() {
      store.proformaInvoicesRefetch?.();
      manualPaymentModalStore.disclosure.onClose();
    },
    ...onError(),
  });

  const handleMatchPayment = async () => {
    store.proformaInvoicesRefetch?.();
  };

  return (
    <React.Fragment>
      <Menu>
        <MenuButton>
          <Icon name="dots-horizontal" color="#02929d" />
        </MenuButton>
        <MenuList>
          {can("outgoingInvoice_markAsPaid") &&
            (!row.paid_at ? (
              <MarkAsPaidButton
                onOpen={() => {
                  manualPaymentModalStore.disclosure.onOpen({
                    input: {
                      date: row.due_date,
                      currency_id: row.currency.id,
                      amount: row.stats.total_amount_with_vat_in_currency,
                      note: row.note ?? "",
                    },
                    showCreateSummaryCheckbox: true,
                    onUpdate: async (createSummaryInvoice) => {
                      await markAsPaidMutation({
                        variables: {
                          id: row.id,
                          date: toApiDate(manualPaymentModalStore.date.start),
                          create_summary_invoice: createSummaryInvoice,
                        },
                      });
                    },
                  });
                }}
              />
            ) : (
              <MenuItem
                onClick={() => {
                  store.markAsUnpaidModal.onOpen({
                    id: row.id,
                    hasMatchedPayments: (row.accountMoves?.length ?? 0) > 0,
                  });
                }}
              >
                <Trans>Mark as unpaid</Trans>
              </MenuItem>
            ))}
          {can("outgoingInvoice_markAsPaid", "bank_read_all") &&
            workspaceStore.settings?.enableBank && (
              <MenuItem
                onClick={() => {
                  matchPaymentModalStore.disclosure.onOpen({
                    selectedMoves: row.accountMoves ?? [],
                    document: {
                      id: row.id,
                      type: BankDocumentTypeEnum.OutgoingProformaInvoice,
                      number: row.invoice_no ?? "",
                      amount: row.stats.total_amount_with_vat_in_currency,
                      currency: row.currency,
                      exchangeRate: row.exchange_rate,
                    },
                    onUpdate: handleMatchPayment,
                  });
                }}
              >
                <Trans>Match payment</Trans>
              </MenuItem>
            )}
          {can([
            "outgoingInvoice_update_all",
            "outgoingInvoice_update_own",
            "outgoingInvoice_update_team",
          ]) && (
            <MenuItemLink
              isDisabled={!!row.paid_at}
              href={{
                pathname: "/invoices/proforma-invoices/edit",
                query: { id: row.id },
              }}
            >
              <Trans>Edit</Trans>
            </MenuItemLink>
          )}
          {can("outgoingInvoice_accessAccountingAttributes") && (
            <MenuItem
              onClick={() => {
                store.changeDatesModalStore.modalState.onOpen({
                  id: row.id,
                  invoice_type: InvoiceTypeEnum.Proforma,
                  issue_date: new Date(row.issue_date),
                  due_date: new Date(row.due_date),
                  paid_at: row.paid_at ? new Date(row.paid_at) : null,
                  onUpdate: () => store.proformaInvoicesRefetch?.(),
                });
              }}
            >
              <Trans>Edit dates</Trans>
            </MenuItem>
          )}
          {can("outgoingInvoice_create") && (
            <MenuItemLink
              href={{
                pathname: "/invoices/proforma-invoices/duplicate",
                query: { id: row.id },
              }}
            >
              <Trans>Duplicate</Trans>
            </MenuItemLink>
          )}
          {can("outgoingInvoice_sendToClient") && (
            <MenuItem
              onClick={() => {
                sendInvoiceModalStore.modalState.onOpen({
                  id: row.id,
                  number: row.invoice_no,
                  type: "proforma",
                  onSubmit: () => {
                    sendInvoiceModalStore.modalState.onClose();
                  },
                });
              }}
            >
              <Trans>Send</Trans>
            </MenuItem>
          )}
          {can("outgoingInvoice_sendToClient") && <ResendAction id={row.id} />}
          <MenuItem
            isDisabled={cannot("outgoingInvoice_delete_all")}
            onClick={() => {
              deleteModal.onOpen();
            }}
          >
            <Trans>Delete</Trans>
          </MenuItem>
        </MenuList>
      </Menu>
      <ModalConfirm
        heading={t`Delete invoice`}
        isOpen={deleteModal.isOpen}
        isLoading={deletingOi}
        destructive
        onClose={deleteModal.onClose}
        onConfirm={async () => {
          try {
            await deleteOi({ id: row.id });
            deleteModal.onClose();
            store.proformaInvoicesRefetch?.();
          } catch {}
        }}
      />
    </React.Fragment>
  );
};
